<template>
  <div>
    <ValidationObserver ref="form" v-slot="{ invalid }">
      <v-overlay :value="loading" absolute>
        <v-progress-circular indeterminate size="64"></v-progress-circular>
      </v-overlay>
      <div v-if="docs">
        <v-row dense>
          <v-col cols="12">
            <ValidationProvider v-slot="{ errors }" name="電話" rules="required">
              <v-text-field
                v-model="docs.phone"
                class="opt-out-phone-field"
                :error="!!errors.length"
                :error-messages="errors"
                :disabled="!isNewRecord"
                :type="isNewRecord ? 'number' : 'text'"
                :step="isNewRecord ? '1' : undefined"
                flat
                label="電話 *"
                outlined
                dense
                hide-details="auto"
                :placeholder="isNewRecord ? '85296614841' : ''"
                @keydown="onPhoneKeydown"
              />
            </ValidationProvider>
          </v-col>
          <v-col cols="12">
            <v-text-field
              v-model="docs.reason"
              flat
              label="原因"
              outlined
              dense
              hide-details="auto"
            />
          </v-col>
          <v-col cols="12">
            <v-select
              v-model="docs.source"
              :items="sourceOptions"
              disabled
              flat
              label="來源"
              outlined
              dense
              hide-details="auto"
            />
          </v-col>
          <v-col v-if="!isNewRecord" cols="12">
            <div class="v-label theme--light mb-1" style="font-size: 12px">關聯客戶／代理</div>
            <v-chip
              v-for="(profile, index) in linkedProfiles"
              :key="profile._id || index"
              class="mr-2 mb-2"
              small
              outlined
            >
              {{ profileDisplayName(profile) }}
            </v-chip>
            <v-alert
              v-if="!linkedProfiles.length"
              type="info"
              outlined
              dense
              class="mt-1 mb-0"
            >
              沒有關聯的客戶／代理
            </v-alert>
          </v-col>
        </v-row>

        <v-row dense class="mt-4">
          <v-col class="d-flex align-center gap-2">
            <v-btn :disabled="invalid || saving" :loading="saving" color="primary" @click="save">
              立即提交
            </v-btn>
            <v-spacer />
            <v-btn :disabled="saving" color="default" outlined @click="resetForm"> 重新填寫 </v-btn>
            <v-btn
              v-if="!isNewRecord"
              :disabled="saving"
              color="error"
              outlined
              @click="confirmDelete"
            >
              刪除
            </v-btn>
          </v-col>
        </v-row>
      </div>
    </ValidationObserver>
  </div>
</template>

<script>
import api from '@/api';
import { crudRead, crudUpdate } from '@/api/crud';
import { RESTFUL } from '@/data/constants';
import ApiError from '@/api/ApiError';

export default {
  name: 'OptOutDetailsForm',
  props: {
    id: {
      type: String,
      default: 'new',
    },
  },
  data() {
    return {
      docs: null,
      loading: false,
      saving: false,
      sourceOptions: [
        { text: 'WhatsApp', value: 'whatsapp' },
        { text: '手動', value: 'manual' },
      ],
    };
  },
  mounted() {
    this.reload();
  },
  computed: {
    isNewRecord() {
      return !this.id || this.id === 'new';
    },
    linkedProfiles() {
      const list = this.docs && this.docs.autocompleteProfiles;
      if (!Array.isArray(list)) return [];
      return list.filter(p => p && typeof p === 'object' && (p.name || p.phone));
    },
  },
  methods: {
    onPhoneKeydown(e) {
      if (!this.isNewRecord) return;
      // Block decimal / sign / exponent and spinner step keys
      if (['.', ',', 'e', 'E', '-', '+', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
      }
    },
    profileDisplayName(profile) {
      const name = profile.name || '';
      const phone = profile.phone || '';
      if (name && phone) return `${name}（${phone}）`;
      return name || phone || '';
    },
    async reload() {
      if (this.isNewRecord) {
        this.docs = {
          phone: '',
          reason: '',
          source: 'manual',
          autocompleteProfiles: [],
        };
      } else {
        this.loading = true;
        try {
          this.docs = await crudRead(api, RESTFUL.optOut.read, this.id, {
            populates: [
              {
                path: 'autocompleteProfiles',
                select: ['name', 'phone', 'type'],
              },
            ],
          });
        } catch (e) {
          const ae = ApiError.wrap(e);
          await this.$store.dispatch('alert/updateAlertMessage', {
            msg: ae.message,
            type: 'error',
            color: 'error',
          });
        } finally {
          this.loading = false;
        }
      }
    },
    resetForm() {
      this.$refs.form?.reset();
      this.reload();
    },
    async confirmDelete() {
      const confirmed = await this.$root.$confirm.open(
        '確認刪除',
        '確定要刪除此拒收紀錄嗎？此操作無法復原。',
        {
          color: 'red',
        },
        '確定刪除',
      );
      if (!confirmed) {
        return;
      }
      this.deleteRecord();
    },
    async deleteRecord() {
      try {
        await api.delete(`${RESTFUL.optOut.delete}/${this.id}`);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '刪除成功',
          type: 'success',
          color: 'success',
        });
        this.$emit('deleted');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      }
    },
    async save() {
      this.saving = true;
      try {
        if (!(await this.$refs.form?.validate?.())) {
          this.saving = false;
          return;
        }

        if (this.isNewRecord) {
          await api.post(RESTFUL.optOut.create, {
            phone: String(this.docs.phone != null ? this.docs.phone : ''),
            reason: this.docs.reason || '',
            source: 'manual',
          });
        } else {
          await crudUpdate(api, RESTFUL.optOut.update, this.id, {
            reason: this.docs.reason || '',
          });
        }

        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: '儲存成功',
          type: 'success',
          color: 'success',
        });
        this.$emit('saved');
      } catch (e) {
        const ae = ApiError.wrap(e);
        await this.$store.dispatch('alert/updateAlertMessage', {
          msg: ae.message,
          type: 'error',
          color: 'error',
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
/* Hide number input step up / step down spinners on create phone */
.opt-out-phone-field >>> input[type='number'] {
  -moz-appearance: textfield;
}
.opt-out-phone-field >>> input[type='number']::-webkit-outer-spin-button,
.opt-out-phone-field >>> input[type='number']::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}
</style>
